import { useState } from "react";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useScriptMode } from "../../../../contexts/ScriptModeContext";
import { useGetGroupFeedQuery } from "../../../../api/groupsApi";
import { periodRange } from "../../../../lib/periodRange";
import PeriodSelect from "./PeriodSelect";
import InsightsSkeleton from "./InsightsSkeleton";
import { rangeLabel, shortDate } from "./insightsFormat";
import { soft } from "./softClasses";
import { useIsDark } from "../../../hatim/soft/SoftKit";
import UserAvatar from "../../../../components/ui/UserAvatar";
import ConnectionState from "../../../../components/ui/ConnectionState";

// Mobildeki öne çıkan ve olay görünümleriyle aynı simge ve renkler;
// darkColor koyu zeminde okunan açık tonu.
const HIGHLIGHT_LOOK = {
  lider: { Icon: EmojiEventsIcon, color: "#C9A227", darkColor: "#E8C766" },
  istikrar: { Icon: LocalFireDepartmentIcon, color: "#A23B3B", darkColor: "#F2A59C" },
  mukemmel: { Icon: StarIcon, color: "#1F7A5C", darkColor: "#7BE3A8" },
  devam: { Icon: EventAvailableIcon, color: "#3B6FA0", darkColor: "#9CC3E8" },
  odak: { Icon: TimerOutlinedIcon, color: "#6B4FA0", darkColor: "#C3AEEB" },
};
const MILESTONE_LOOK = {
  streak: { Icon: LocalFireDepartmentIcon, color: "#C9A227", darkColor: "#E8C766" },
  perfect: { Icon: CheckCircleIcon, color: "#1F7A5C", darkColor: "#7BE3A8" },
  join: { Icon: PersonAddAlt1Icon, color: "#3B6FA0", darkColor: "#9CC3E8" },
};

const toneFor = (look, dark) => (dark ? { ...look, color: look.darkColor } : look);

/**
 * Enler (yönetici) ve Olaylar (üye): dönem özeti üstte sabit; altında öne
 * çıkanlar satır satır ve son hareketler zaman çizgisi olarak kayar.
 * Satıra basınca o dalda grubun tamamının sıralaması açılır.
 */
export default function GroupHighlightsView({ groupId }) {
  const { t } = useScriptMode();
  const dark = useIsDark();
  const [period, setPeriod] = useState("thisWeek");
  const [openKey, setOpenKey] = useState(null);
  const range = periodRange(period);
  const { data, isFetching, isError, error, refetch } = useGetGroupFeedQuery(
    { groupId, startDate: range.startDate, endDate: range.endDate },
    { skip: !groupId }
  );
  const feed = data?.data;

  if (isError) {
    return (
      <ConnectionState
        error={error}
        onRetry={refetch}
        retrying={isFetching}
        message={t("Grup verileri yüklenemedi")}
        compact
      />
    );
  }
  if (!feed || isFetching) return <InsightsSkeleton layout="highlights" rows={5} />;

  const { week, milestones = [] } = feed;
  const rate = Math.max(0, Math.min(100, week.completionRate || 0));
  const stats = [
    { value: String(week.perfectDays || 0), label: t("mükemmel gün") },
    { value: `${week.activeMembers || 0}/${week.memberCount || 0}`, label: t("aktif") },
    { value: String(week.longestStreak?.streak || 0), label: t("En uzun seri") },
  ];
  const valueText = (h) => (h.suffix === "%" ? `%${h.value}` : `${h.value} ${t(h.suffix)}`);
  const standingsFor = (h) =>
    [...(week.standings || [])].sort((a, b) => (b[h.field] || 0) - (a[h.field] || 0));

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Üstte sabit özet */}
      <div className={`${soft.summary} flex flex-col gap-3.5`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <PeriodSelect value={period} onChange={setPeriod} />
            <span className={`text-sm font-medium truncate ${soft.ink}`}>
              {rangeLabel(week.startDate, week.endDate)}
            </span>
          </div>
          <span className={`text-3xl font-light ${soft.accent}`}>%{rate}</span>
        </div>
        <div className={soft.track}>
          <div className={soft.fill} style={{ width: `${rate}%` }} />
        </div>
        <div className="flex">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`flex-1 flex flex-col items-center ${
                i > 0 ? "border-l border-terakkiBorder dark:border-white/15" : ""
              }`}
            >
              <span className={`text-lg font-medium ${soft.ink}`}>{s.value}</span>
              <span className={`text-xs ${soft.muted}`}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {week.highlights?.length ? (
          <section>
            <div className={soft.sectionTitle}>{t("Öne çıkanlar")}</div>
            {week.highlights.map((h) => {
              const look = toneFor(HIGHLIGHT_LOOK[h.key] || HIGHLIGHT_LOOK.lider, dark);
              const open = openKey === h.key;
              return (
                <div key={h.key}>
                  <button
                    type="button"
                    data-highlight={h.key}
                    aria-expanded={open}
                    onClick={() => setOpenKey(open ? null : h.key)}
                    className={soft.rowButton}
                  >
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${look.color}1F`, color: look.color }}
                    >
                      <look.Icon sx={{ fontSize: 18 }} aria-hidden />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-xs ${soft.muted}`}>{h.label}</span>
                      <span
                        className={`block text-[15px] truncate ${
                          h.user ? `font-medium ${soft.ink}` : `italic ${soft.accent}`
                        }`}
                      >
                        {h.user
                          ? `${h.user.name} ${h.user.surname}`
                          : h.text || `${h.tiedCount} üye başa baş !`}
                      </span>
                    </span>
                    <span className="text-[15px] font-medium" style={{ color: look.color }}>
                      {valueText(h)}
                    </span>
                  </button>
                  {open ? (
                    <div className="px-5 py-3 bg-terakkiBrandCloud/60 dark:bg-white/5 border-b border-terakkiBorder dark:border-white/15">
                      <p className={`text-xs mb-2 ${soft.muted}`}>{h.description}</p>
                      <ol className="flex flex-col gap-1">
                        {standingsFor(h).map((s, i) => (
                          <li key={s.id} className="flex items-center gap-2 text-sm">
                            <span className={`w-5 text-right ${soft.faint}`}>{i + 1}</span>
                            <UserAvatar person={s} size={22} />
                            <span
                              className={`flex-1 truncate ${
                                s.isMe ? `font-medium ${soft.accent}` : soft.ink
                              }`}
                            >
                              {s.name} {s.surname}
                            </span>
                            <span className={soft.muted}>
                              {h.suffix === "%" ? `%${s[h.field] || 0}` : `${s[h.field] || 0} ${t(h.suffix)}`}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </section>
        ) : null}

        {/* Son hareketler: zaman çizgisi */}
        <section>
          <div className={soft.sectionTitle}>{t("Son hareketler")}</div>
          {milestones.length === 0 ? (
            <div className={soft.empty}>
              <AutoAwesomeOutlinedIcon aria-hidden />
              {t("Henüz hareket yok")}
            </div>
          ) : (
            <ol className="px-5 pt-1.5 pb-8">
              {milestones.map((m, i) => {
                const look = toneFor(MILESTONE_LOOK[m.type] || MILESTONE_LOOK.perfect, dark);
                const last = i === milestones.length - 1;
                return (
                  <li key={m.id} className="flex gap-3.5">
                    <span className="flex flex-col items-center w-2.5 pt-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: look.color }} />
                      {!last ? (
                        <span className="flex-1 w-px mt-1 bg-terakkiBorder dark:bg-white/15" />
                      ) : null}
                    </span>
                    <span className={`flex-1 ${last ? "" : "pb-4"}`}>
                      <span className={`block text-sm leading-5 ${soft.ink}`}>{m.text}</span>
                      <span className={`block text-xs mt-0.5 ${soft.faint}`}>{shortDate(m.date)}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
